"use client";

import { env } from "@/lib/env";
import { isNavigatorOnline, subscribeToNavigatorOnline } from "./network";

const PROBE_TIMEOUT_MS = 5_000;
const RETRY_DELAY_MS = 10_000;
const listeners = new Set<() => void>();
let online: boolean | undefined;
let unsubscribeNavigator: (() => void) | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let activeProbe: AbortController | undefined;

export function isBackendOnline(): boolean {
  return online ?? isNavigatorOnline();
}

function setOnline(value: boolean): void {
  if (online === value) return;
  online = value;
  for (const listener of listeners) listener();
}

function cancelProbe(): void {
  clearTimeout(retryTimer);
  retryTimer = undefined;
  activeProbe?.abort();
  activeProbe = undefined;
}

async function probe(): Promise<void> {
  if (activeProbe || online || !isNavigatorOnline() || listeners.size === 0) {
    return;
  }
  clearTimeout(retryTimer);
  retryTimer = undefined;
  const controller = new AbortController();
  activeProbe = controller;
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    // localhost can respond with no internet. Probe the same backend that
    // serves auth/data, without refreshing a session or sending user cookies.
    const response = await fetch(
      `${env.NEXT_PUBLIC_SUPABASE_URL.replace(/\/$/, "")}/auth/v1/health`,
      {
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
        headers: { apikey: env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY },
        signal: controller.signal,
      },
    );
    await response.arrayBuffer();
    if (response.ok && !controller.signal.aborted && isNavigatorOnline()) {
      setOnline(true);
    }
  } catch {
    // Keep the warning until a bounded recovery attempt succeeds.
  } finally {
    clearTimeout(timeout);
    // An offline event or the last unsubscribe may have canceled this probe.
    // Its completion must not schedule work for a newer subscription.
    if (activeProbe === controller) {
      activeProbe = undefined;
      if (!isBackendOnline() && isNavigatorOnline() && listeners.size > 0) {
        retryTimer = setTimeout(() => void probe(), RETRY_DELAY_MS);
      }
    }
  }
}

function handleNetworkChange(): void {
  if (!isNavigatorOnline()) {
    cancelProbe();
    setOnline(false);
  } else if (!online) {
    void probe();
  }
}

// Both the root toast and authenticated notification drawer subscribe here.
// They share one recovery attempt, and the final unsubscribe owns cleanup.
export function subscribeToBackendOnline(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    online = isNavigatorOnline();
    unsubscribeNavigator = subscribeToNavigatorOnline(handleNetworkChange);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      unsubscribeNavigator?.();
      unsubscribeNavigator = undefined;
      cancelProbe();
      online = undefined;
    }
  };
}
